<template>
  <base-modal ref="$modal" :loading="isLoading" fullscreen-on-mobile @on-close="onBaseModalClose">
    <div ref="$content" class="contact-info">
      <template v-if="!isLoading">
      <div class="contact-info__header">
        <div class="contact-info__header-left">
          <div class="contact-info__field">
            <span class="contact-info__field-name">{{ $t('name_short') }}:&nbsp;</span>
            <span class="contact-info__field-value">{{ clientInfo.Name }}</span>
          </div>
          <div class="contact-info__field">
            <span class="contact-info__field-name">{{ $t('position') }}:&nbsp;</span>
            <span class="contact-info__field-value">{{ clientInfo.Post }}</span>
          </div>
          <div class="contact-info__field">
            <span class="contact-info__field-name"
              >{{ $t('contact_info') }}:
            </span>
            <span
              class="contact-info__field-value"
              v-html="clientInfo.Contacts"
            />
          </div>
          <div class="contact-info__field">
            <span class="contact-info__field-name">{{ $t('address_upcase') }}:&nbsp;</span>
            <span class="contact-info__field-value">{{
              clientInfo.Address?.join(", ") || ""
            }}</span>
          </div>
          <div class="contact-info__field">
            <span class="contact-info__field-name">{{ $t('tags_upcase') }}:&nbsp;</span>
            <span class="contact-info__field-value">{{ tagsString }}</span>
          </div>
          <div class="contact-info__field">
            <span class="contact-info__field-name">{{ $t('created_label') }}:&nbsp;</span>
            <span class="contact-info__field-value">{{ createDate }}</span>
          </div>
          <div
            v-if="clientInfo.Changelog?.length"
            class="contact-info__field contact-info__field--col"
          >
            <div
				class="contact-info__field-name"
				:style="{ color: '#4D4D4D' }"
            >
              {{ $t('changed_label') }}:
            </div>
            <div
              v-for="(change, index) in showAllChanges
                ? clientInfo.Changelog
                : clientInfo.Changelog.slice(0, 4)"
              class="contact-info__field-value-col"
              :key="change.Time"
            >
              <span class="contact-info__field-value-col-time"
                >{{ getDate(change.Time) }}&nbsp;</span
              >
              <span class="contact-info__field-value-col-name"
                >({{ change.Worker }})</span
              >
              <span class="contact-info__field-value-col-action">{{
                getChangeDescription(change)
              }}</span>
              <div
                v-if="
                  !showAllChanges &&
                  clientInfo.Changelog.length > 4 &&
                  index === 3
                "
                class="contact-info__field-value-col-show-more"
                @click="showAllChanges = true"
              >
                {{ $t('more_count_label', { count: clientInfo.Changelog.length - 4 }) }}
              </div>
            </div>
          </div>
          <div
            v-if="clientInfo.ConnectedClients?.length"
            class="contact-info__field contact-info__field--col"
          >
            <div
				class="contact-info__field-name"
				:style="{ color: '#4D4D4D' }"
            >
              {{ $t('clients_label') }}:
            </div>
            <div
              v-for="client in clientInfo.ConnectedClients"
              class="contact-info__field-value-col"
              :key="client.Id"
            >
              <router-link
                class="contact-info__field-value-col-link"
                :to="{ query: { page: 'client', id: client.Id } }"
                @click="$modal?.onClose()"
              >
                {{ client.Name }}
              </router-link>
            </div>
          </div>
          <div
            v-if="clientInfo.ConnectedProjects?.length"
            class="contact-info__field contact-info__field--col"
          >
            <div
				class="contact-info__field-name"
				:style="{ color: '#4D4D4D' }"
            >
              {{ $t('contact_attached_projects') }}:
            </div>
            <div
              v-for="project in clientInfo.ConnectedProjects"
              class="contact-info__field-value-col"
              :key="project.Id"
            >
              <router-link
                class="contact-info__field-value-col-link"
                :to="{ query: { page: 'project', id: project.Id } }"
                @click="$modal?.onClose()"
              >
                {{ project.Name }}
              </router-link>
            </div>
          </div>
        </div>
        <div class="contact-info__header-right">
          <div class="contact-info__add-lists">
            <app-resizable-dropdown
              position="left"
              :resizable="false"
              :initial-width="320"
              :initial-height="436"
              :close-by-click-outside="false"
              :content-z-index="10003"
              :mobile-title="$t('choose_lists')"
              ref="$createEditFileModal"
              name="$createFileModal"
              @close="showAddListSelect = false"
            >
              <template #activator>
                <app-tooltip :text="$t('add_to_list')">
                  <template #activator>
                    <app-icon
                      class="contact-info__add-lists-btn"
                      name="plus"
                      width="14px"
                      color="#979797"
                      hover-color="#499259"
                      cursor="pointer"
                      @click="showAddListSelect = true"
                    />
                  </template>
                </app-tooltip>
              </template>
              <main-info-list-select
                :id="clientInfo?.Id"
                :added-lists="clientInfo?.AnalyticsLists"
                show-close-icon
                :entity-type="3"
                @close="closeAddClientDropdown"
                @add-list="updateAnalyticLists"
              />
            </app-resizable-dropdown>
            <div class="contact-info__add-lists-text">{{ $t('file_info_lists') }}:</div>
          </div>
          <div class="contact-info__lists">
            <div
              v-for="list in clientInfo?.AnalyticsLists"
              class="contact-info__lists-item"
              :key="list.Id"
            >
              <!-- <div class="contact-info__lists-item-dot" /> -->
              <div class="contact-info__lists-item-name">
                {{ list.Name }}
              </div>
              <app-icon
                class="contact-info__lists-item-delete"
                name="close"
                width="12px"
                color="#D2D2D2"
                hover-color="#499259"
                cursor="pointer"
                @click="onDeleteList(list)"
              />
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>
  </base-modal>
</template>

<script setup lang="ts">
import BaseModal from "@/components/common/modal/BaseModal.vue";
import { computed, ref, watch } from "vue";
import { i18n } from "@/i18n";
import { PersonApi } from "@/ api/PersonApi";
import { AnalyticsListItem, ChangelogItem, ClientInfo } from "@/store/clients";
import moment from "moment";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { AnalyticsApi } from "@/ api/AnalyticsApi";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import MainInfoListSelect from "../../listSelect/MainInfoListSelect.vue";
import { useModalEscape } from "@/composables/useModalEscape";

const changeDescriptionKeys: Record<string, string> = {
  AddressChanged: "contact_change_address",
  ClientsChanged: "contact_change_clients",
  ContactsChanged: "contact_change_contacts",
  NameChanged: "contact_change_name",
  PostChanged: "contact_change_post",
  TagsChanged: "contact_change_tags",
};

const $modal = ref<typeof BaseModal | null>(null);
const isLoading = ref(false);
const $content = ref<HTMLElement | null>(null);
const clientInfo = ref<ClientInfo | null>(null);
const showAllChanges = ref(false);
const showAddListSelect = ref(false);
const lists = ref<AnalyticsListItem[]>([]);
const selectedList = ref<AnalyticsListItem | null>(null);
const listsNotFound = ref(false);
const $createEditFileModal = ref<typeof AppResizableDropdown | null>(null);

const closeAddClientDropdown = () => {
  showAddListSelect.value = false;
  $createEditFileModal.value?.close();
};

const updateAnalyticLists = async () => {
  if (!clientInfo.value) return;

  clientInfo.value.AnalyticsLists = (
    await AnalyticsApi.getListOfElement(clientInfo.value.Id, 3)
  ).Lists;
};

const tagsString = computed(() => {
  if (!clientInfo.value || !clientInfo.value.Tags) {
    return "";
  }

  return clientInfo.value.Tags.map((tag) => `#${tag}`).join(", ");
});

const mappedLists = computed(() =>
  lists.value.map((list) => ({
    label: list.Name,
    value: list.Id,
    list,
  })),
);

const createDate = computed(() => {
  if (!clientInfo.value || !clientInfo.value.CreateTime) {
    return "";
  }

  return `${moment(clientInfo.value.CreateTime).format("HH:mm DD.MM.YYYY")}${
    clientInfo.value.Creator ? ` (${clientInfo.value.Creator})` : ""
  }`;
});

const getDate = (date: string) => {
  return moment(date).format("HH:mm DD.MM.YYYY");
};

const getChangeDescription = (change: ChangelogItem) => {
  const changedFieldEntry = Object.entries(change).filter(
    (entry) => typeof entry[1] === "boolean" && entry[1],
  );
  return changedFieldEntry.length
    ? String(i18n.global.t(changeDescriptionKeys[changedFieldEntry[0][0] as string]))
    : "";
};

const onDeleteList = async (list: AnalyticsListItem) => {
  if (!clientInfo.value) return;
  await AnalyticsApi.removeElementIntoList(list.Id, 4, clientInfo?.value?.Id);
  clientInfo.value.AnalyticsLists.splice(
    clientInfo.value?.AnalyticsLists.findIndex(
      (listFile) => listFile.Id === list.Id,
    ),
    1,
  );
};

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
  content: $content,
  close: () => $modal.value?.onClose(),
  intercept: () => {
    if (!showAddListSelect.value) {
      return false;
    }
    closeAddClientDropdown();
    return true;
  }
});

const onOpen = async (Id: number) => {
  try {
    isLoading.value = true;
    $modal.value?.onOpen();
    activateEsc();
    clientInfo.value = await PersonApi.getClientInfo(Id);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};


const onBaseModalClose = () => {
  deactivateEsc();
  closeAddClientDropdown();
};

watch(showAddListSelect, async (newVal) => {
  if (!newVal) {
    return;
  }

  const list = await AnalyticsApi.getLists(
    "",
    0,
    3,
    clientInfo.value?.Id as number,
  );

  lists.value = list.Lists;
});

watch(selectedList, (newVal) => {
  console.log(newVal);
});

defineExpose({ onOpen });
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.contact-info {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px;

  &__header {
    display: flex;
    width: 100%;
    gap: 8px;

    &-left,
    &-right {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &-left {
      flex: 2;
    }

    &-right {
      flex: 1;
    }
  }

  &__field {
    margin-bottom: 4px;
    font-size: 14px;
    line-height: 18px;
    color: $color-text_main;

    &--col {
      display: flex;
      flex-direction: column;
    }

    &-name {
      font-weight: 500;
    }

    &-value {
      color: $color-text_main;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    &-value-col {
      display: inline-block;
      align-items: center;
      overflow-wrap: anywhere;
      word-break: break-word;

      &-time {
        color: #979797;
      }

      &-name {
        color: $color-text_main;
      }

      &-action {
        margin-left: 2px;
        color: $color-text_main;
      }

      &-show-more {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
        font-size: 12px;
        line-height: 16px;
        border-radius: 8px;
        margin-left: 6px;
        margin-top: 1px;
        background-color: #F2F2F2;
        color: #979797;
        cursor: pointer;

        &:hover {
          background-color: #F3F8F4;
          color: #438451;
        }
      }

      &-link {
        font-size: 14px;
        line-height: 16px;
        color: #499259;
        text-decoration: none;
        margin-bottom: 4px;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &__add-lists {
    display: flex;
    align-items: center;
    gap: 6px;

    &-text {
      color: $color-text_main;
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
    }

    &-btn {
      margin-top: -2px;
    }
  }

  &__lists {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 2px;
	max-width: 290px;

    &-item {
      display: inline-flex;
      align-items: center;
      font-size: 14px;
      line-height: 18px;
      gap: 2px;
      color: $color-text_main;

      &-dot {
        height: 1px;
        width: 1px;
        border-radius: 100%;
      }
    }
  }
}
</style>
